import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'
import ReactRailsUJS from 'react_ujs'
import { debounce } from 'underscore'

export default class Autosave extends Controller {
  static values = {
    url: String,
    finalizeUrl: String
  }

  connect() {
    this.firstRequest = true
    this.requestsInFlight = 0
    this.updatedInputNames = new Array()

    this.element.querySelectorAll('input, textarea, select').forEach(element => {
      element.addEventListener('change', this.inputChanged)
    })

    // React Components are mounted after Autosave is connected.
    window.addEventListener('react-date-time:mounted', debounce(this.attachReactDateTimePickerListeners, 300))

    document.addEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.addEventListener('turbo:before-render', this.unmmountReactComponents)

    console.log('SuperForm: auto-save enabled.')
  }

  disconnect() {
    this.element.querySelectorAll('input, textarea, select').forEach(element => {
      element.removeEventListener('change', this.inputChanged)
    })

    this.element.querySelectorAll('input[type="hidden"].react-date-time').forEach(element => {
      element.removeEventListener('react-date-time:changed', this.inputChanged)
    })

    window.removeEventListener('react-date-time:mounted', this.attachReactDateTimePickerListeners)

    document.removeEventListener('turbo:before-morph-element', this.preventInputElementJitter)
    document.removeEventListener('turbo:before-render', this.unmmountReactComponents)
  }

  attachReactDateTimePickerListeners = () => {
    this.element.querySelectorAll('input[type="hidden"].react-date-time').forEach(element => {
      element.addEventListener('change', this.inputChanged)
    })
  }

  inputChanged = event => {
    let target = event.target
    let elementName = target.name

    let elements = []
    if (target.classList.contains('super-form__basic-list')) {
      let listTable = target.closest('table')
      elements = listTable.querySelectorAll('input')
    } else {
      elements = this.element.querySelectorAll(`[name="${elementName}"]`)
    }

    // Remove error classes from elements because jitter protection will prevent them from updating.
    elements.forEach(el => el.classList.remove('is-invalid'))

    if (target.dataset['superFormCalculatedField'] != 'true') {
      this.updatedInputNames.push(Array.from(elements).map(el => el.name))
      // Pop the oldest input name group off the updatedInputNames stack after 3 seconds.
      setTimeout(() => {this.updatedInputNames.shift() }, 3000)
    }

    const data = new FormData()
    // Append all the relevant elements to the FormData object.
    Array.from(elements).forEach(el => {
      if (el.type == 'checkbox' || el.type == 'radio') {
        if (el.checked) {
          data.append(el.name, el.value)
        }
      } else {
        data.append(el.name, el.value)
      }
    })

    // Use the first Autosave request to send all the initial values of the ReactDateTimePickers.
    if (this.firstRequest) {
      this.element.querySelectorAll('input[type="hidden"].react-date-time').forEach(element => {
        data.append(element.name, element.value)
      })
    }

    const request = new FetchRequest('patch', this.urlValue, {
      body: data,
      responseKind: 'json'
    })

    this.withRequestCount(request.perform()).then(response => {
      document.dispatchEvent(new CustomEvent('super-form:autosave:started', { detail: { request: request } }))

      if (response.ok) {
        document.dispatchEvent(new CustomEvent('super-form:autosave:success', { detail: { response: response } }))
      }
    }).catch(error => {
      console.error('SuperForm: autosave failed', error)
      document.dispatchEvent(new CustomEvent('super-form:autosave:failed', { detail: { error: error } }))
    })
  }

  // Prevents jitter when an input element is morphed by Turbo.
  preventInputElementJitter = event => {
    const detail = event.detail
    const newEl = detail.newElement
    if (!newEl?.name) return

    const target = event.target

    const isInputRecentlyUpdated = this.updatedInputNames.some(list => list.includes(newEl.name))
    const isUpdatedInputActive = !target.readOnly && target.name === document.activeElement.name


    if (isInputRecentlyUpdated || isUpdatedInputActive) {
      event.preventDefault()
      console.log(`SuperForm#preventInputElementJitter: prevented jitter for ${newEl.name}`)
    }
  }

  // If there are no requests in flight, finalize the form. Otherwise, wait for the last autosave request to complete.
  finalize = () => {
    if (this.requestsInFlight === 0) {
      this.requestFinalize()
    } else {
      document.addEventListener('turbo:load', () => this.requestFinalize(), { once: true })
    }
  }

  requestFinalize = () => {
    this.element.action = this.finalizeUrlValue
    this.element.requestSubmit()
  }

  unmmountReactComponents = () => ReactRailsUJS.unmountComponents()

  withRequestCount = (request) => {
    this.requestsInFlight++

    return request.then(response => {
      this.firstRequest = false
      return response
    }).finally(() => {
      this.requestsInFlight--
    })
  }
}

export { Autosave }
